import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "primereact/utils";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { translationKeys } from "../../../../../models/app-translation-keys";
import { UploadFile } from "../../../../../models/classes/upload-file.class";
import { fileSizeToString } from "../../../ValuePipes/file-size-to-string.function";
import { splitFileName } from "../../../ValuePipes/split-file-name-type.function";

import "./SimpleFileList.scss";

import { UploadFileListItem } from "../../../../../models/classes/upload-file-list-item";
import { ActionButton } from "../../../Buttons";
import { ShowTooltipWhenTextTruncated } from "../../../ShowTooltipWhenTruncated";

interface ISimpleFileListProps {
  uploadFiles: UploadFile[];
  handleRemoveFile: (file: UploadFile) => void;
  smallFileList?: boolean;
  progressingFileIds?: string[];
}

export const SimpleFileListComponent: FunctionComponent<ISimpleFileListProps> = (props: ISimpleFileListProps) => {
  const { t } = useTranslation();

  const [uploadFileListItems, setUploadFileListItems] = useState<UploadFileListItem[]>([]);

  useEffect(() => {
    setUploadFileListItems(prevs => {
      const progressById = new Map(prevs.map(item => [item.uploadFile.id, item.isProgressing]));
      return props.uploadFiles.map(
        uploadFile =>
          new UploadFileListItem({
            uploadFile,
            isProgressing:
              props.progressingFileIds?.includes(uploadFile.id) ?? progressById.get(uploadFile.id) ?? false,
          }),
      );
    });
  }, [props.uploadFiles, props.progressingFileIds]);

  if (props.uploadFiles?.length < 1) {
    return;
  }

  return (
    <div
      className={classNames("simple-file-list-container", {
        "small-file-list": props.smallFileList,
        "multiple-files": props.uploadFiles.length > 1,
      })}
    >
      {uploadFileListItems
        .filter(uploadFile => !!uploadFile)
        .map((uploadFile, index) => (
          <div
            className={classNames("simple-file-list-item", {
              "has-error": !!uploadFile?.uploadFile.errors?.length,
            })}
            key={index}
          >
            <div className="file-list-item-header">
              <div className="file-icon">
                <FontAwesomeIcon icon={uploadFile?.uploadFile.icon} />
              </div>

              <div className="file-info">
                <div className="file-name-type">
                  <ShowTooltipWhenTextTruncated
                    forceShowTooltip={false}
                    tooltipPosition="top"
                    tooltipContent={uploadFile.uploadFile.file.name}
                  >
                    <span className="file-name">{splitFileName(uploadFile.uploadFile.file, "name")}</span>
                  </ShowTooltipWhenTextTruncated>
                  .<span>{splitFileName(uploadFile.uploadFile.file, "type")}</span>
                </div>

                <small className="file-description">{fileSizeToString(uploadFile.uploadFile.fileSizeInByte)}</small>
              </div>

              <ActionButton
                icon={faXmark}
                hideBackground={true}
                tooltipDisabled={!uploadFile.isProgressing}
                tooltip={t(translationKeys.upload.fileInProgress)}
                tooltipPosition="top"
                buttonDisabled={uploadFile.isProgressing}
                onClick={() => props.handleRemoveFile(uploadFile.uploadFile)}
                customClasses="file-delete-icon"
              />
            </div>

            {!!uploadFile.uploadFile.errors?.length && (
              <div className="file-list-item-errors">
                {uploadFile.uploadFile.errors.map((error: string, index: any) => (
                  <div key={index}>{t(`upload.error.${error}`)}</div>
                ))}
              </div>
            )}
          </div>
        ))}
    </div>
  );
};
